/**
 * MyReports — shows the current user's submitted reports for a given type.
 * data-testid: candeza-reports-myreports-*
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import {
  useMyReports,
  useRefillStaffReport,
  type ReportType,
  type StaffReport,
} from './api';
import { api } from '@core/api/client';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ReportCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
`;

const ReportInfo = styled.div`
  flex: 1;
`;

const ReportTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const ReportMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Empty = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

interface MyReportsProps {
  reportType: ReportType;
  onRefillOpened?: (newDraft: StaffReport) => void;
}

export const MyReports: React.FC<MyReportsProps> = ({ reportType, onRefillOpened }) => {
  const { data, isLoading } = useMyReports(reportType);
  const refillMutation = useRefillStaffReport(reportType);
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownloadPdf = async (report: StaffReport) => {
    if (!report.pdf_ready) {
      toast.error('PDF not ready. Please try again shortly.');
      return;
    }
    setDownloading(report.id);
    try {
      const result = await api.get<{ download_url: string; expires_at: string }>(
        `/v1/reports/staff/${reportType}/${report.id}/pdf`,
      );
      // Open the signed GCS URL in a new tab
      window.open(result.download_url, '_blank');
    } catch {
      toast.error('Failed to get PDF download link.');
    } finally {
      setDownloading(null);
    }
  };

  const handleRefill = async (report: StaffReport) => {
    try {
      const newDraft = await refillMutation.mutateAsync(report.id) as StaffReport;
      toast.success('New draft created from this report');
      onRefillOpened?.(newDraft);
    } catch {
      toast.error('Failed to create re-fill draft');
    }
  };

  if (isLoading) {
    return <div data-testid="candeza-reports-myreports-loading">Loading reports…</div>;
  }

  const reports = data?.items ?? [];

  if (!reports.length) {
    return (
      <Empty data-testid="candeza-reports-myreports-empty">
        No submitted reports yet.
      </Empty>
    );
  }

  return (
    <Container data-testid="candeza-reports-myreports-list">
      {reports.map((report) => (
        <ReportCard
          key={report.id}
          data-testid={`candeza-reports-myreports-row-${report.id}`}
        >
          <ReportInfo>
            <ReportTitle>
              {report.period_start} – {report.period_end} ({report.period_type})
              {report.student_name && ` — ${report.student_name}`}
            </ReportTitle>
            <ReportMeta>
              Submitted: {report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : '—'} ·{' '}
              {report.pdf_ready ? '✅ PDF ready' : '⏳ PDF pending'}
            </ReportMeta>
          </ReportInfo>
          <Actions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadPdf(report)}
              disabled={!report.pdf_ready || downloading === report.id}
              data-testid={`candeza-reports-myreports-download-${report.id}`}
            >
              {downloading === report.id ? 'Loading…' : '⬇ PDF'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRefill(report)}
              disabled={refillMutation.isPending}
              data-testid={`candeza-reports-myreports-refill-${report.id}`}
            >
              🔁 Re-fill
            </Button>
          </Actions>
        </ReportCard>
      ))}
    </Container>
  );
};

